import translations from '$lib/translations/survey-player.yaml';
import type { InterpretedSurvey } from '$lib/utils/surveyInterpreter';

type SurveyScreen = InterpretedSurvey['screens'][number];

/**
 * Centralized function to extract a display title from a survey screen
 * Prioritizes header.title > header.image.alt > questionId > translated "Question" + fallback
 * @param screen - The survey screen object
 * @param index - The screen index for fallback display
 * @param language - The language code for translation
 * @returns The display title for the screen
 */
export function getScreenDisplayTitle(
  screen: SurveyScreen | undefined | null, 
  index: number, 
  language?: string
): string {
  if (!screen) {
    const currentTranslations = language ? (translations[language] || translations.en_us) : translations.en_us;
    return `${currentTranslations.question} ${index}`;
  }

  // Prioritize header.title first
  if (screen.header?.title && screen.header.title.trim() !== '') {
    return screen.header.title;
  }

  // Then check header.image.alt
  if (screen.header?.image?.alt && screen.header.image.alt.trim() !== '') {
    return screen.header.image.alt;
  }

  // Then use questionId if available
  if (screen.questionId) {
    return screen.questionId;
  }

  // Fallback to translated "Question" + index
  const currentTranslations = language ? (translations[language] || translations.en_us) : translations.en_us;
  return `${currentTranslations.question} ${index}`;
}

/**
 * Helper function to get a unique identifier for deduplication purposes
 * @param screen - The survey screen object
 * @param index - The screen index for fallback
 * @returns A unique identifier string
 */
export function getScreenIdentifier(screen: SurveyScreen, index: number): string {
  if (screen.questionId) {
    return `question:${screen.questionId}`;
  }
  
  if (screen.type === 'submit') {
    return 'type:submit';
  }
  
  if (screen.type === 'screen') {
    return 'type:screen';
  }
  
  if (screen.type === 'profile') {
    return 'type:profile';
  }
  
  // Fallback
  return screen.id || `screen_${index}`;
}

/**
 * Deduplicate an array of screens by their identifier, keeping the first occurrence
 * @param screens - Array of survey screens
 * @returns Deduplicated array of screens
 */
export function deduplicateScreens(screens: SurveyScreen[]): SurveyScreen[] {
  const seenIdentifiers = new Set<string>();

  return screens.filter((screen, index) => {
    const identifier = getScreenIdentifier(screen, index);

    if (seenIdentifiers.has(identifier)) {
      return false; // Skip duplicate
    }

    seenIdentifiers.add(identifier);
    return true;
  });
}

/**
 * Check if screen is displayable (not hidden)
 * @param screen - The survey screen object
 * @returns True if screen should be displayed
 */
export function isDisplayableScreen(screen: SurveyScreen): boolean {
  return screen.data?.hidden !== true;
}

/**
 * Check if screen should be included in stats/charts
 * @param screen - The survey screen object
 * @returns True if screen can be charted/analyzed
 */
export function isChartableScreen(screen: SurveyScreen): boolean {
  if (!isDisplayableScreen(screen)) return false;

  // Include questions with questionType (except text)
  if (screen.type === 'question' && screen.questionType && screen.questionType !== 'text') {
    return true;
  }
  // Include disaggregate screens (except profile)
  if (screen.data?.disaggregate === true && screen.type !== 'profile') {
    return true;
  }
  return false;
} 